import { useState } from 'react';
import camelCase from 'lodash/fp/camelCase';
import flow from 'lodash/fp/flow';
import get from 'lodash/fp/get';
import getOr from 'lodash/fp/getOr';
import groupBy from 'lodash/fp/groupBy';
import mapKeys from 'lodash/fp/mapKeys';
import { format, parseISO } from 'date-fns';

import './ShoppingCartSummary.scss';
import { useTranslation } from '../../../hooks';
import {
	ShoppingCartItem,
	ShoppingCartItinerary,
	LoadingIndicator,
	NoHotelProductsWarning,
	EventProductsOutsideDates,
} from '../index';
import { MODALS } from '@micro-apps/shared';
import { formatDateRange } from '@micro-apps/shared';
import { formatPrice, getNumberOfGuests } from '@fshr/shared';
import Icon from '../../Icon';
import { translate } from '@micro-apps/env';
import Carousel from '../../Carousel/Carousel';
import Cta from '../../Cta';
import Image from '../../Image';

const DEFAULT_ITINERARY_DISCLAIMER =
	'Our concierge team will craft the best itinerary for you based on your request. Please note that your request is not confirmed until a concierge contacts you directly to finalize the details. Charges will only be applied once your request is confirmed.';

/**
 * @param {Object} ShoppingCartSummaryProps - .
 * @param {Object} ShoppingCartSummaryProps.officialName
 * @param {Object} ShoppingCartSummaryProps.booking
 * @param {Object} ShoppingCartSummaryProps.basket
 * @param {Object} ShoppingCartSummaryProps.eventProductsOutsideStayDates
 * @param {string | undefined} ShoppingCartSummaryProps.estimatedTotalDisclaimer
 * @param {any} ShoppingCartSummaryProps.removeFromCart
 * @param {any} ShoppingCartSummaryProps.showPriceBreakdown
 * @param {() => void | undefined} ShoppingCartSummaryProps.removeBooking
 * @param {boolean} ShoppingCartSummaryProps.insideCart
 * @param {boolean} ShoppingCartSummaryProps.noHotelProduct
 * @param {string} ShoppingCartSummaryProps.accomPath
 * @param {any} ShoppingCartSummaryProps.recommendedCategories
 */
export default function ShoppingCartSummary({
	officialName,
	booking,
	basket,
	eventProductsOutsideStayDates,
	estimatedTotalDisclaimer,
	removeFromCart,
	showPriceBreakdown,
	removeBooking = undefined,
	insideCart = false,
	FourSeasonsLogo,
	noHotelProduct = false,
	accomPath,
	recommendedCategories,
	isMpeJourney,
	mpeDetails,
	itineraryDisclaimer = DEFAULT_ITINERARY_DISCLAIMER,
}) {
	const { t } = useTranslation();
	const [isSubmitting, setIsSubmitting] = useState(false);

	const {
		transportation = [],
		departureTransportation = [],
		arrivalAmenities = [],
		experiences = [],
		dining = [],
		spa = [],
	} = flow(groupBy('onRequestEventProductType'), mapKeys(camelCase))(basket);

	if (departureTransportation.length) {
		transportation.push(...departureTransportation);
	}

	const itinerary = spa.concat(experiences, dining);

	const handleRemoveItem = ({ productId }) => {
		setIsSubmitting(true);
		removeFromCart({ productId }).then(() => setIsSubmitting(false));
	};

	const totalPrice =
		isMpeJourney && mpeDetails?.estimatedTotal
			? formatPrice({ value: mpeDetails.estimatedTotal, currencyCode: mpeDetails.currencyCode })
			: flow(get(['price', 'total', 'cash']), ({ currencyCode, amount }) =>
					formatPrice({ value: amount, currencyCode })
				)(booking);

	const propertyBillingTotalPrice = isMpeJourney
		? null
		: flow(
				get(['price', 'total', 'cash']),
				({ propertyBillingCurrencyCode, propertyBillingAmount }) => {
					if (!propertyBillingCurrencyCode && !propertyBillingAmount) return null;
					return formatPrice({
						value: propertyBillingAmount,
						currencyCode: propertyBillingCurrencyCode,
					});
				}
			)(booking);

	const sameCurrencyInPrices = flow(
		get(['price', 'total', 'cash']),
		({ currencyCode, propertyBillingCurrencyCode }) => currencyCode === propertyBillingCurrencyCode
	)(booking);

	const {
		checkInDate,
		checkOutDate,
		price: hotelProductPrice,
	} = getOr({}, ['hotelProducts', 0])(booking);

	const formattedStayDateRange =
		checkInDate && checkOutDate && formatDateRange(checkInDate, checkOutDate);

	const taxesFeesCharges = get(['prepay', 'taxesFeesCharges'])(hotelProductPrice);

	const handleShowInfoModal = (hotelProduct, totalPrice) => {
		showPriceBreakdown({
			name: MODALS.BREAKDOWN,
			hotelProduct,
			totalPrice,
		});
	};

	return (
		<>
			{isSubmitting && <LoadingIndicator />}

			<div className="shopping-cart-summary">
				<div className="shopping-cart-summary__fs-title">
					{FourSeasonsLogo}

					{isMpeJourney ? (
						<div className="ty-b1">{t(mpeDetails?.title)}</div>
					) : officialName?.__html ? (
						<div className="ty-b1" dangerouslySetInnerHTML={{ __html: officialName?.__html }}></div>
					) : null}

					{checkInDate && checkOutDate && (
						<div className="shopping-cart-summary__fs-title__date">{formattedStayDateRange}</div>
					)}
				</div>

				{insideCart && noHotelProduct && <NoHotelProductsWarning accomPath={accomPath} />}

				{booking.hotelProducts.length > 0 && (
					<div className="shopping-cart-summary__group">
						{!insideCart && <h4 className="ty-h4">{isMpeJourney ? t('Journey') : t('Stay')}</h4>}

						<div className="shopping-cart-summary__group__items shopping-cart-summary__group__items__stay">
							{booking.hotelProducts.map(hotelProduct => {
								const {
									guestCount: { numberAdults = 0, numberChildren = 0 },
									roomRate,
									roomTypes,
									price,
									productType,
									duration,
								} = hotelProduct;

								// For MPE journeys, use properties from mpeDetails instead of roomTypes
								if (isMpeJourney && mpeDetails?.properties) {
									// For MPE, show duration instead of rate plan title
									const cartDescription = duration
										? `${duration} Night${duration > 1 ? 's' : ''}`
										: roomRate.ratePlanTitle;

									// For MPE, show total guests instead of adults & children breakdown
									const numberOfPeople = `${numberAdults} Guest${numberAdults !== 1 ? 's' : ''}`;

									return (
										<ShoppingCartItem
											key="mpe-property"
											name={mpeDetails.properties.map(property => property.name).join(', ')}
											cartDescription={cartDescription}
											roomRate={roomRate}
											numberOfPeople={numberOfPeople}
											price={mpeDetails.basePrice}
											currencyCode={mpeDetails.currencyCode}
											taxAndFees={mpeDetails.feesDisclaimer}
											removeItem={removeBooking}
											insideCart={insideCart}
											productType={productType}
											onRequestEventProductType={insideCart ? t('Stay') : ''}
											isMpeJourney={isMpeJourney}
										/>
									);
								}

								// Regular booking flow (non-MPE)
								return roomTypes.map((roomType, index) => {
									const totalRoomRatePrice = hotelProduct?.price?.prepay?.base?.cash?.amount;
									const taxDisclaimer = () =>
										booking.resortFeesEnabled
											? (insideCart
													? hotelProduct?.roomRate?.feesDisclaimerPreCheckout
													: hotelProduct?.roomRate?.feesDisclaimerCheckout) ||
												hotelProduct?.roomRate?.feesDisclaimer
											: hotelProduct?.roomRate?.feesDisclaimer || undefined;
									return (
										<ShoppingCartItem
											key={roomType.roomTypeId + index}
											name={roomType.description}
											cartDescription={roomRate.ratePlanTitle}
											roomRate={roomRate}
											numberOfPeople={getNumberOfGuests({
												t,
												numberOfAdults: numberAdults,
												numberOfChildren: numberChildren,
											})}
											price={totalRoomRatePrice}
											stayResortFees={hotelProduct?.price?.prepay?.stayResortFees}
											currencyCode={price.total.cash.currencyCode}
											taxAndFees={taxDisclaimer()}
											removeItem={removeBooking}
											insideCart={insideCart}
											productType={productType}
											image={roomType.image}
											onRequestEventProductType={insideCart ? t('Stay') : ''}
										/>
									);
								});
							})}
						</div>
					</div>
				)}

				{transportation.length > 0 && (
					<div className="shopping-cart-summary__group">
						<h4 className="ty-h4">
							{t('Transportation')}
							{departureTransportation && '**'}
						</h4>
						<div className="shopping-cart-summary__group__items">
							{transportation.map(({ productId, name, productTotal, taxAndFees, images }) => (
								<ShoppingCartItem
									key={productId}
									name={
										name
											? name === 'Departure Drop-Off'
												? t('Departure Drop-Off')
												: t('Arrival Pick-up')
											: ''
									}
									price={productTotal.totalPrice.amount}
									currencyCode={productTotal.totalPrice.currencyCode}
									taxAndFees={taxAndFees}
									removeItem={() =>
										handleRemoveItem({
											productId,
										})
									}
									insideCart={insideCart}
									image={images?.[0]?.url}
								/>
							))}
						</div>
					</div>
				)}

				{arrivalAmenities.length > 0 && (
					<div className="shopping-cart-summary__group">
						<h4 className="ty-h4 shopping-cart-summary__amenities">{t('In Your Room')}</h4>
						<div className="shopping-cart-summary__group__items">
							{arrivalAmenities.map(
								({
									productId,
									name,
									cartDescription,
									productTotal,
									terms,
									ticketOptionType,
									images,
									details,
								}) => {
									const requestDate = details.fields.find(obj => obj.name === 'Request_Date');
									const formattedRequestDate = requestDate
										? format(parseISO(requestDate.value), 'MMM d')
										: null;

									return (
										<ShoppingCartItem
											key={productId}
											name={name}
											cartDescription={cartDescription}
											price={productTotal.totalPrice.amount}
											currencyCode={productTotal.totalPrice.currencyCode}
											terms={terms}
											quantity={ticketOptionType.totalCount}
											removeItem={() =>
												handleRemoveItem({
													productId,
												})
											}
											insideCart={insideCart}
											image={images?.[0]?.url}
											requestDate={formattedRequestDate}
										/>
									);
								}
							)}
						</div>
					</div>
				)}

				{itinerary.length > 0 && (
					<div className="shopping-cart-summary__group">
						{insideCart ? (
							<h4 className="ty-c4 shopping-cart-summary__itinerary">{t('Itinerary Requests')}*</h4>
						) : (
							<h4 className="ty-h4">{t('Itinerary Requests')}**</h4>
						)}

						<ShoppingCartItinerary
							itinerary={itinerary}
							removeItem={handleRemoveItem}
							insideCart={insideCart}
							handleShowInfoModal={handleShowInfoModal}
						/>
					</div>
				)}

				{eventProductsOutsideStayDates && !noHotelProduct && (
					<EventProductsOutsideDates
						eventProductsOutsideDates={eventProductsOutsideStayDates}
						formattedStayDateRange={formattedStayDateRange}
						removeItem={handleRemoveItem}
						handleShowInfoModal={handleShowInfoModal}
					/>
				)}

				{itinerary.length > 0 && insideCart && (
					<div className="cart-summary-footnote">
						{'* '}
						{itineraryDisclaimer}
					</div>
				)}

				{!insideCart && (
					<div className="exposedTaxesAndFees">
						{/* For MPE journeys, show only Base Taxes, Taxes, and Estimate Total */}
						{isMpeJourney && mpeDetails ? (
							<>
								{mpeDetails.taxes?.map((tax, index) => (
									<div className="exposedTaxesAndFees__pair" key={`mpe-tax-${index}`}>
										<span className="ty-b3">{tax.description}</span>
										<span className="exposedTaxesAndFees__pair__price">
											{formatPrice({ value: tax.amount, currencyCode: mpeDetails.currencyCode })}
										</span>
									</div>
								))}
							</>
						) : (
							/* Regular booking flow - show tRetail taxes */
							taxesFeesCharges?.map(({ code, description, cash }) => {
								return (
									<div className="exposedTaxesAndFees__pair" key={code}>
										<span className="ty-b3">{description}</span>
										<span className="exposedTaxesAndFees__pair__price">
											{formatPrice({
												currencyCode: cash.currencyCode,
												value: cash.amount,
											})}
										</span>
									</div>
								);
							})
						)}
					</div>
				)}
				{!booking.eventProducts.length ? (
					<div className="Cart-ctaSection">
						{insideCart && (
							<>
								<p className="ty-c4">
									<Icon icon="plus_sign"></Icon>
									{translate('Add Itinerary Requests')}
								</p>
								<p className="ty-d4">{translate('Treat yourself to something special.')}</p>
							</>
						)}
						<Carousel
							id="FilterCategories"
							showDots={false}
							elements={
								recommendedCategories && [
									...recommendedCategories.map((category, i) => (
										<Cta type="primary" link={category.categoryPath}>
											<div className="product-title ty-c3">{category.categoryName}</div>
											<div className="product-additional-text ty-c4">
												{category.categoryAddText}
											</div>
											<Image
												className="recommended-item__image recommended-item__image__portrait"
												src={category.portraitImageUrl}
												alt={category.categoryName}
											/>
										</Cta>
									)),
								]
							}
						></Carousel>
					</div>
				) : (
					<div className="Cart-ctaNoImage">
						<Carousel
							id="FilterCategories"
							showDots={false}
							elements={
								recommendedCategories && [
									...recommendedCategories.map((category, i) => (
										<Cta type="text" className="ty-d4" link={category.categoryPath}>
											<Icon icon="plus_sign" />
											<p className="ty-d4">Add {category.categoryName}</p>
										</Cta>
									)),
								]
							}
						></Carousel>
					</div>
				)}

				{insideCart && (
					<div
						className="shopping-cart-summary__taxes-fees"
						data-cy="shopping-cart-summary__taxes-fees"
					>
						<span className="ty-b2">{t('Taxes & Fees')}</span>
						<span className="ty-c3">{t('To be calculated at checkout')}</span>
					</div>
				)}

				<div className="shopping-cart-summary__total">
					<span className="ty-b1 shopping-cart__total__text">
						{t('Est. Total')}
						{!insideCart ? '*' : ''}
					</span>
					<span className="shopping-cart-summary__total__price">{totalPrice}</span>
				</div>

				{propertyBillingTotalPrice && !insideCart && !sameCurrencyInPrices && (
					<div className="detailD4 shopping-cart-summary__property-billing">
						<span className="shopping-cart-summary__property-billing__text">
							{t('Property billing currency:')}
						</span>
						<span className="shopping-cart-summary__property-billing__text">
							{propertyBillingTotalPrice}
						</span>
					</div>
				)}
			</div>

			{(estimatedTotalDisclaimer || itinerary.length > 0) && (
				<div className="cart-summary-footnote">
					{departureTransportation?.length || (itinerary.length > 0 && !insideCart) ? (
						<div>
							**
							{itineraryDisclaimer}
						</div>
					) : null}

					{estimatedTotalDisclaimer && (
						<div
							dangerouslySetInnerHTML={{ __html: estimatedTotalDisclaimer }}
							className="estimated-total-disclaimer"
						/>
					)}
				</div>
			)}
		</>
	);
}
